import React, { useState, useEffect } from 'react';
import { SubFeatureGate } from './FeatureGate';
import api from '../api';
import { Briefcase, CalendarCheck, Award, Building2, Plus, Filter, ArrowLeft, Eye, Clock, MapPin, IndianRupee, FileText, CheckCircle, XCircle, ExternalLink, Users, CheckSquare, Square, Layers, ListChecks, Search, Lightbulb } from 'lucide-react';
import '../pages/FacultyDashboard.css';
import UIElementGuard from './UIElementGuard';
import SkillScoreEditor from './tpo/SkillScoreEditor';
import { buildSkillPayload, mergeGeneratedScores, parseSkillNames, scoresFromStoredSkills, updateSkillScoreMap } from '../utils/jobSkillScores';

const PlacementDrivesModule = ({ showToast, onViewCandidates, targetDriveJobId, setTargetDriveJobId }) => {
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [selectedBatches, setSelectedBatches] = useState(['All']);
  const [selectedJobForDrive, setSelectedJobForDrive] = useState(null);
  
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const storedRole = localStorage.getItem('role');
  const isCustomRole = storedRole !== 'university' && storedRole !== 'admin' && (storedUser.role_id || storedUser.base_role === 'custom_role' || storedUser.base_role === 'role');
  
  const hasPermission = (perm) => {
    if (!isCustomRole) return true;
    if (storedUser.jwt_permissions && storedUser.jwt_permissions.includes(perm)) return true;
    return false;
  };
  const [selectedCompanyForDrive, setSelectedCompanyForDrive] = useState(null);
  const [selectedDriveId, setSelectedDriveId] = useState(null);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showUpdateBatchModal, setShowUpdateBatchModal] = useState(false);

  // Bulk selection state
  const [bulkSelectedJobIds, setBulkSelectedJobIds] = useState([]);

  // Drill-down hierarchy navigation state
  const [activeCompanyCard, setActiveCompanyCard] = useState(null); // null = Company Grid view, object = Company Roles list
  const [selectedJobDetailModal, setSelectedJobDetailModal] = useState(null); // job object for Level 3 detailed modal
  const [jobSkillDraft, setJobSkillDraft] = useState({ must: {}, good: {} });
  const [jobSkillScoring, setJobSkillScoring] = useState(false);
  const [jobSkillSaving, setJobSkillSaving] = useState(false);
  
  // Modals state
  const [jobToReject, setJobToReject] = useState(null);
  const [showUnscheduleModal, setShowUnscheduleModal] = useState(false);
  
  // Company filter bar inside Available Jobs view
  const [selectedCompanyFilter, setSelectedCompanyFilter] = useState('ALL');

  // Add Company & Add Job Role modals
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', industry: 'Technology & AI', description: '', website: '' });
  const [newJob, setNewJob] = useState({ job_name: '', company_id: '', description: '' });

  // Internal tab state to switch between available jobs and scheduled drives
  const [innerTab, setInnerTab] = useState('drives'); // 'drives', 'scheduled', 'all_scheduled'
  const [scheduledDrives, setScheduledDrives] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(false);
  const [scheduledSearchTerm, setScheduledSearchTerm] = useState('');
  const [activeScheduledCompanyCard, setActiveScheduledCompanyCard] = useState(null);
  const [driveToUnschedule, setDriveToUnschedule] = useState(null);

  useEffect(() => {
    fetchCompanies();
    fetchJobs();
    fetchBatches();
    fetchScheduledDrives();
  }, []);

  useEffect(() => {
    setActiveScheduledCompanyCard(null);
  }, [innerTab]);

  // Deep linking logic
  useEffect(() => {
    if (targetDriveJobId && jobs.length > 0 && companies.length > 0) {
      const job = jobs.find(j => j.id === targetDriveJobId);
      if (job) {
        const company = companies.find(c => c.id === job.company_id);
        if (company) {
          setInnerTab('drives');
          setActiveCompanyCard(company);
          setSelectedJobDetailModal(job);
        }
      }
      if (setTargetDriveJobId) {
        setTargetDriveJobId(null); // Reset after opening
      }
    }
  }, [targetDriveJobId, jobs, companies, setTargetDriveJobId]);

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const res = await api.get('/tpo/companies');
      const formattedCompanies = (res.data || []).map(comp => ({
        ...comp,
        name: comp.name ? comp.name.replace(/\b\w/g, l => l.toUpperCase()) : comp.name
      }));
      setCompanies(formattedCompanies);
    } catch (err) {
      console.error("Error fetching companies", err);
    } finally {
      setLoadingCompanies(false);
    }
  };


  // Seed the score editor whenever a job modal opens; the stored values carry
  // their provenance (vendor / ai / tpo) so the TPO sees what is already set.
  useEffect(() => {
    if (selectedJobDetailModal) {
      setJobSkillDraft(scoresFromStoredSkills(
        selectedJobDetailModal.must_have_skills,
        selectedJobDetailModal.good_to_have_skills,
      ));
    }
  }, [selectedJobDetailModal]);

  const handleJobSkillChange = (group, name, raw) => {
    setJobSkillDraft((prev) => ({
      ...prev,
      [group]: updateSkillScoreMap(prev[group], name, raw),
    }));
  };

  const generateJobSkillScores = async () => {
    if (!selectedJobDetailModal) return;
    setJobSkillScoring(true);
    try {
      const res = await api.post('/tpo/jobs/score-skills', { job_id: selectedJobDetailModal.id });
      const toMap = (rows) => Object.fromEntries(
        (rows || [])
          .filter((row) => row && row.skill)
          .map((row) => [
            String(row.skill).trim().toLowerCase(),
            { score: row.required_score, source: row.score_source === 'tpo' ? 'tpo' : 'ai' },
          ])
      );
      setJobSkillDraft((prev) => mergeGeneratedScores(prev, {
        must: toMap(res.data?.must_have_skills),
        good: toMap(res.data?.good_to_have_skills),
      }));
    } catch (err) {
      console.error('Job requirement scoring failed', err);
      // Surface what the server actually said. The old catch-all message hid a
      // 403/409/400 and made a permissions or ownership problem look like a
      // broken feature, which is how the same report kept coming back.
      const detail = err?.response?.data?.detail;
      const reason = Array.isArray(detail)
        ? detail.map((item) => item?.msg || item).join('; ')
        : (typeof detail === 'string' ? detail : '');
      const status = err?.response?.status;
      showToast(
        reason
          ? `Couldn't generate requirement scores: ${reason}`
          : `Couldn't generate the required score automatically${status ? ` (HTTP ${status})` : ''}. You can enter it manually.`,
        'error'
      );
    } finally {
      setJobSkillScoring(false);
    }
  };

  const saveJobSkillScores = async () => {
    const job = selectedJobDetailModal;
    if (!job) return;
    setJobSkillSaving(true);
    try {
      const res = await api.put(`/tpo/jobs/${job.id}/skill-scores`, {
        must_have_skills: buildSkillPayload(parseSkillNames(job.must_have_skills), jobSkillDraft.must),
        good_to_have_skills: buildSkillPayload(parseSkillNames(job.good_to_have_skills), jobSkillDraft.good),
      });
      const updated = {
        ...job,
        must_have_skills: res.data?.must_have_skills ?? job.must_have_skills,
        good_to_have_skills: res.data?.good_to_have_skills ?? job.good_to_have_skills,
        revision_pending: false,
      };
      setSelectedJobDetailModal(updated);
      setJobs((prev) => prev.map((item) => (item.id === job.id ? { ...item, ...updated } : item)));
      showToast('Requirement scores updated');
    } catch (err) {
      console.error('Saving job requirement scores failed', err);
      showToast(err?.response?.data?.detail || 'Could not save requirement scores.');
    } finally {
      setJobSkillSaving(false);
    }
  };

  const jobScoreReadOnly = ['REJECTED', 'CLOSED'].includes(
    String(selectedJobDetailModal?.status || '').toUpperCase()
  );

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const res = await api.get('/tpo/jobs');
      const formattedJobs = (res.data || [])
        .filter(job => job.status !== 'REJECTED')
        .map(job => ({
          ...job,
          company_name: job.company_name ? job.company_name.replace(/\b\w/g, l => l.toUpperCase()) : job.company_name
        }));
      setJobs(formattedJobs);
    } catch (err) {
      console.error("Error fetching jobs", err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const syncMantrikaJobs = async () => {
    setLoadingJobs(true);
    try {
      const res = await api.post('/tpo/jobs/mantrika-sync');
      showToast(`Synced successfully! ${res.data.new_jobs || 0} new jobs found.`);
      await fetchJobs(); // refresh the list
    } catch (err) {
      console.error("Error syncing mantrika jobs", err);
      showToast("Error syncing Mantrika jobs", "error");
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleRejectJob = (job) => {
    setJobToReject(job);
  };

  const confirmRejectJob = async () => {
    if (!jobToReject) return;
    try {
      await api.put(`/tpo/jobs/${jobToReject.id}/status`, { status: 'REJECTED' });
      showToast('Job rejected successfully.');
      setJobToReject(null);
      await fetchJobs();
    } catch (err) {
      console.error("Error rejecting job", err);
      showToast('Error rejecting job', 'error');
    }
  };

  const fetchBatches = async () => {
    try {
      const res = await api.get('/tpo/batches');
      setAvailableBatches(res.data || []);
    } catch (err) {
      console.error("Error fetching batches", err);
    }
  };

  const fetchScheduledDrives = async () => {
    setLoadingScheduled(true);
    try {
      const res = await api.get('/tpo/scheduled-drives');
      const formattedDrives = (res.data || []).map(drive => ({
        ...drive,
        company_name: drive.company_name ? drive.company_name.replace(/\b\w/g, l => l.toUpperCase()) : drive.company_name
      }));
      setScheduledDrives(formattedDrives);
    } catch (err) {
      console.error("Error fetching scheduled drives", err);
    } finally {
      setLoadingScheduled(false);
    }
  };

  const handleBatchToggle = (label) => {
    if (label === 'All') {
      if (selectedBatches.includes('All')) {
        setSelectedBatches([]);
      } else {
        setSelectedBatches(['All']);
      }
    } else {
      let newBatches = [...selectedBatches];
      
      if (newBatches.includes('All')) {
        newBatches = availableBatches.map(b => b.label);
      }
      
      if (newBatches.includes(label)) {
        newBatches = newBatches.filter(b => b !== label);
      } else {
        newBatches.push(label);
      }
      
      if (newBatches.length === availableBatches.length && availableBatches.length > 0) {
        newBatches = ['All'];
      }
      
      setSelectedBatches(newBatches);
    }
  };

  const handleToggleBulkJob = (jobId, e) => {
    if (e) e.stopPropagation();
    if (bulkSelectedJobIds.includes(jobId)) {
      setBulkSelectedJobIds(bulkSelectedJobIds.filter(id => id !== jobId));
    } else {
      setBulkSelectedJobIds([...bulkSelectedJobIds, jobId]);
    }
  };

  const handleSelectCompanyJobs = (companyId, e) => {
    if (e) e.stopPropagation();
    const compJobs = jobs.filter(j => j.company_id === companyId && !j.is_scheduled).map(j => j.id);
    const combined = Array.from(new Set([...bulkSelectedJobIds, ...compJobs]));
    setBulkSelectedJobIds(combined);
  };

  const handleUnselectCompanyJobs = (companyId, e) => {
    if (e) e.stopPropagation();
    const compJobIds = jobs.filter(j => j.company_id === companyId).map(j => j.id);
    setBulkSelectedJobIds(bulkSelectedJobIds.filter(id => !compJobIds.includes(id)));
  };

  const handleSelectAllJobs = (e) => {
    if (e) e.stopPropagation();
    const allActiveJobIds = jobs.filter(j => !j.is_scheduled).map(j => j.id);
    setBulkSelectedJobIds(allActiveJobIds);
  };

  const handleUnselectAllJobs = (e) => {
    if (e) e.stopPropagation();
    setBulkSelectedJobIds([]);
  };

  const handleScheduleDrive = async () => {
    const isBulk = selectedJobForDrive === 'BULK' || (bulkSelectedJobIds.length > 0 && !selectedJobForDrive);
    if (!isBulk && (!selectedCompanyForDrive || !selectedJobForDrive)) {
      if (showToast) showToast('Please select a company and job role first', 'error');
      return;
    }
    try {
      if (isBulk) {
        if (bulkSelectedJobIds.length === 0) {
          if (showToast) showToast('No job roles selected for bulk scheduling', 'error');
          return;
        }
        await api.post('/tpo/schedule-drive', {
          job_ids: bulkSelectedJobIds,
          company_id: selectedCompanyForDrive || null,
          target_batch: selectedBatches.join(', ')
        });
        if (showToast) showToast(`Successfully scheduled placement drives for ${bulkSelectedJobIds.length} job role(s)!`, 'success');
        setBulkSelectedJobIds([]);
      } else {
        await api.post('/tpo/schedule-drive', {
          job_id: selectedJobForDrive,
          company_id: selectedCompanyForDrive,
          target_batch: selectedBatches.join(', ')
        });
        if (showToast) showToast('Placement Drive scheduled successfully!', 'success');
      }
      setShowBatchModal(false);
      setSelectedJobForDrive(null);
      setSelectedCompanyForDrive(null);
      setSelectedBatches(['All']);
      fetchJobs();
      fetchCompanies();
      fetchScheduledDrives();
    } catch (err) {
      console.error("Error scheduling drive", err);
      if (showToast) showToast(err.response?.data?.detail || 'Failed to schedule drive', 'error');
    }
  };

  const handleAddCompany = async (e) => {
    e.preventDefault();
    if (!newCompany.name.trim()) {
      if (showToast) showToast('Please enter company name', 'error');
      return;
    }
    try {
      await api.post('/tpo/companies', newCompany);
      if (showToast) showToast('Company added successfully!', 'success');
      setShowAddCompanyModal(false);
      setNewCompany({ name: '', industry: 'Technology & AI', description: '', website: '' });
      fetchCompanies();
    } catch (err) {
      console.error(err);
      if (showToast) showToast(err.response?.data?.detail || 'Failed to add company', 'error');
    }
  };

  const handleAddJob = async (e) => {
    e.preventDefault();
    if (!newJob.job_name.trim() || !newJob.company_id) {
      if (showToast) showToast('Please select a company and enter job name', 'error');
      return;
    }
    try {
      await api.post(`/tpo/companies/${newJob.company_id}/jobs`, {
        job_name: newJob.job_name,
        description: newJob.description
      });
      if (showToast) showToast('Job role added successfully!', 'success');
      setShowAddJobModal(false);
      setNewJob({ job_name: '', company_id: '', description: '' });
      fetchJobs();
      fetchCompanies();
    } catch (err) {
      console.error(err);
      if (showToast) showToast(err.response?.data?.detail || 'Failed to add job', 'error');
    }
  };

  const handleUpdateBatch = async () => {
    try {
      await api.put(`/tpo/schedule-drive/${selectedDriveId}`, { target_batch: selectedBatches.join(', ') });
      if (showToast) showToast('Batch updated successfully!', 'success');
      setShowUpdateBatchModal(false);
      fetchJobs();
      fetchScheduledDrives();
    } catch (err) {
      console.error(err);
      if (showToast) showToast(err.response?.data?.detail || 'Failed to update batch', 'error');
    }
  };

  const handleUnscheduleDrive = (driveId) => {
    setDriveToUnschedule(driveId);
    setShowUnscheduleModal(true);
  };

  const confirmUnscheduleDrive = async () => {
    if (!driveToUnschedule) return;
    try {
      await api.delete(`/tpo/schedule-drive/${driveToUnschedule}`);
      if (showToast) showToast('Job unapproved successfully!', 'success');
      fetchJobs();
      fetchScheduledDrives();
      setShowUnscheduleModal(false);
      setDriveToUnschedule(null);
    } catch (err) {
      console.error(err);
      if (showToast) showToast(err.response?.data?.detail || 'Failed to unschedule drive', 'error');
    }
  };

  const filteredScheduledDrives = scheduledDrives.filter(drive => {
    if (!scheduledSearchTerm) return true;
    const term = scheduledSearchTerm.toLowerCase();
    return (
      (drive.company_name && drive.company_name.toLowerCase().includes(term)) ||
      (drive.job_name && drive.job_name.toLowerCase().includes(term))
    );
  });

  const groupedScheduledDrives = filteredScheduledDrives.reduce((acc, drive) => {
    const compName = drive.company_name || 'University Placement Office';
    if (!acc[compName]) acc[compName] = [];
    acc[compName].push(drive);
    return acc;
  }, {});

  return (
    <div className="placement-drives-module" style={{
      '--faculty-bg': '#f7fafc',
      '--faculty-panel': '#ffffff',
      '--faculty-panel-soft': '#f8fbff',
      '--faculty-border': '#dbe5ef',
      '--faculty-border-strong': '#c6d4e3',
      '--faculty-text': '#061329',
      '--faculty-muted': '#4a5d73',
      '--faculty-faint': '#7c8da2',
      '--faculty-accent': '#05a8f3',
      '--faculty-accent-soft': '#e8f7ff',
      '--card-bg': '#ffffff',
      '--faculty-bg-alt': '#f0f7ff',
    }}>
      <div className="placement-drives-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '15px' }}>
        <div style={{
          display: 'inline-flex',
          background: '#f0f4f8',
          borderRadius: '12px',
          padding: '5px',
          border: '1px solid #d8e3ed',
          gap: '4px',
        }}>
        <button
          onClick={() => setInnerTab('drives')}
          style={{
            padding: '9px 22px',
            borderRadius: '9px',
            border: innerTab === 'drives' ? '1.5px solid #05a8f3' : '1.5px solid transparent',
            background: innerTab === 'drives' ? '#ffffff' : 'transparent',
            color: innerTab === 'drives' ? '#05a8f3' : '#2d3a4a',
            fontWeight: '700',
            fontSize: '0.9rem',
            cursor: 'pointer',
            transition: 'all 0.18s',
            boxShadow: innerTab === 'drives' ? '0 1px 6px rgba(5,168,243,0.12)' : 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Available Jobs ({jobs.filter(j => !j.is_scheduled).length})
        </button>
        <button
          onClick={() => setInnerTab('scheduled')}
          style={{
            padding: '9px 22px',
            borderRadius: '9px',
            border: innerTab === 'scheduled' ? '1.5px solid #05a8f3' : '1.5px solid transparent',
            background: innerTab === 'scheduled' ? '#ffffff' : 'transparent',
            color: innerTab === 'scheduled' ? '#05a8f3' : '#2d3a4a',
            fontWeight: '700',
            fontSize: '0.9rem',
            cursor: 'pointer',
            transition: 'all 0.18s',
            boxShadow: innerTab === 'scheduled' ? '0 1px 6px rgba(5,168,243,0.12)' : 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Approved Jobs ({scheduledDrives.length})
        </button>
      </div>
      
      <div>
        <UIElementGuard page="placement_drives" element="sync_jobs_btn">
          <SubFeatureGate feature="mantrika" sub="mantrika_job_sync">
          <button
            onClick={syncMantrikaJobs}
            disabled={loadingJobs}
            style={{
              background: 'linear-gradient(135deg, #05a8f3, #3b82f6)',
              color: '#fff',
              border: 'none',
              padding: '9px 18px',
              borderRadius: '9px',
              fontWeight: '600',
              cursor: loadingJobs ? 'wait' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              opacity: loadingJobs ? 0.7 : 1,
              boxShadow: '0 4px 10px rgba(5, 168, 243, 0.2)'
            }}
          >
            {loadingJobs ? (
              <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>↻</span>
            ) : (
              <span style={{ fontSize: '1.2em' }}>↻</span>
            )}
            Sync Jobs
          </button>
          </SubFeatureGate>
        </UIElementGuard>
      </div>
      </div>

      {innerTab === 'drives' && (
        <div>
          {/* Outer header removed to reduce crowding */}

          {/* Prominent Bulk Action Banner */}
          {bulkSelectedJobIds.length > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #0284c7 0%, #05a8f3 50%, #38bdf8 100%)',
              color: '#ffffff',
              padding: '12px 20px',
              borderRadius: '16px',
              marginBottom: '28px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '20px',
              boxShadow: '0 12px 32px rgba(5, 168, 243, 0.28), inset 0 1px 0 rgba(255,255,255,0.3)',
              border: '1.5px solid rgba(255, 255, 255, 0.4)',
              position: 'relative',
              overflow: 'hidden',
              animation: 'fadeIn 0.3s ease-out'
            }}>
              {/* Decorative background glow circle */}
              <div style={{ position: 'absolute', right: '-40px', top: '-40px', width: '180px', height: '180px', background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)', pointerEvents: 'none' }} />

              <div style={{ display: 'flex', alignItems: 'center', gap: '18px', zIndex: 1 }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '12px',
                  background: '#ffffff',
                  color: '#0284c7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '800',
                  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.12)',
                  flexShrink: 0
                }}>
                  <ListChecks size={22} />
                </div>
                <div>
                  <div style={{
                    fontSize: '1.15rem',
                    fontWeight: '900',
                    color: '#ffffff',
                    letterSpacing: '-0.3px',
                    lineHeight: '1.25',
                    textShadow: '0 1px 3px rgba(0,0,0,0.15)'
                  }}>
                    {bulkSelectedJobIds.length} {bulkSelectedJobIds.length === 1 ? 'Job Role' : 'Job Roles'} Selected for Bulk Scheduling
                  </div>
                  <div style={{
                    margin: '2px 0 0',
                    fontSize: '0.85rem',
                    color: '#f0f9ff',
                    fontWeight: '600',
                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}>
                    Ready to schedule campus placement recruitment events across multiple job profiles in one click!
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap', zIndex: 1 }}>
                <button
                  type="button"
                  onClick={handleUnselectAllJobs}
                  style={{
                    background: 'rgba(255, 255, 255, 0.22)',
                    border: '1.5px solid rgba(255, 255, 255, 0.5)',
                    padding: '8px 14px',
                    borderRadius: '10px',
                    color: '#ffffff',
                    fontWeight: '700',
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.2s',
                    backdropFilter: 'blur(4px)'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.22)'; }}
                >
                  Unselect All ({bulkSelectedJobIds.length})
                </button>

                <UIElementGuard page="placement_drives" element="schedule_drive_btn">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCompanyForDrive(null);
                      setSelectedJobForDrive('BULK');
                      setSelectedBatches(['All']);
                      setShowBatchModal(true);
                    }}
                    style={{
                      background: '#ffffff',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '10px',
                      color: '#0284c7',
                      fontWeight: '900',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '10px',
                      boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                      transition: 'all 0.2s transform'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 26px rgba(0, 0, 0, 0.24)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.15)'; }}
                  >
                    <CalendarCheck size={16} style={{ color: '#0284c7' }} /> Approve Bulk ({bulkSelectedJobIds.length} Roles)
                  </button>
                </UIElementGuard>
              </div>
            </div>
          )}

          {/* Level 1 vs Level 2 Drill-down Navigation */}
          {!activeCompanyCard ? (
            /* LEVEL 1: Corporate Partners Grid (Companies First with Number of Openings) */
            <div className="faculty-panel" style={{ padding: '24px', borderRadius: '16px', border: '1px solid var(--faculty-border)', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px', borderBottom: '1px solid var(--faculty-border)', paddingBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '800', color: 'var(--faculty-text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Building2 size={24} style={{ color: '#05a8f3' }} /> Corporate Partners & Active Openings
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: 'var(--faculty-muted)' }}>
                    Select a company below to explore their posted roles ('{jobs.filter(j => !j.is_scheduled).length}' total openings across '{companies.length}' corporate partners)
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    className="faculty-btn-secondary"
                    style={{ padding: '8px 14px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    onClick={handleSelectAllJobs}
                  >
                    <CheckSquare size={15} /> Bulk Select ({jobs.filter(j => !j.is_scheduled).length})
                  </button>
                  {bulkSelectedJobIds.length > 0 && (
                    <button
                      className="faculty-btn-secondary"
                      style={{ padding: '8px 14px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#ef4444', borderColor: '#fca5a5' }}
                      onClick={handleUnselectAllJobs}
                    >
                      Unselect All
                    </button>
                  )}
                  <button
                    className="faculty-btn-secondary"
                    style={{ padding: '8px 14px', fontSize: '0.85rem' }}
                    onClick={() => {
                      setActiveCompanyCard({ id: 'ALL', name: 'All Corporate Partners', industry: 'All Sectors' });
                    }}
                  >
                    View All Roles Combined
                  </button>
                  <UIElementGuard page="placement_drives" element="schedule_drive_btn">
                    <button
                      className="faculty-btn-primary"
                      style={{ padding: '8px 16px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      onClick={() => {
                        setSelectedCompanyForDrive(selectedCompanyFilter === 'ALL' ? (companies[0]?.id || null) : selectedCompanyFilter);
                        setSelectedJobForDrive(null);
                        setSelectedBatches(['All']);
                        setShowBatchModal(true);
                      }}
                    >
                      <Plus size={16} /> Approve Job
                    </button>
                  </UIElementGuard>
                </div>
              </div>

              {loadingCompanies ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--faculty-muted)' }}>Loading corporate partners...</div>
              ) : companies.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 20px', background: 'var(--faculty-bg-alt)', borderRadius: '14px', border: '1px dashed var(--faculty-border)' }}>
                  <Building2 size={48} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: 'var(--faculty-text)' }}>No Corporate Partners Yet</h4>
                  <p style={{ color: 'var(--faculty-muted)', margin: '0 0 20px' }}>Add your first hiring partner or direct them to our public job portal.</p>
                  <button className="faculty-btn-primary" onClick={() => setShowAddCompanyModal(true)}>+ Onboard First Company</button>
                </div>
              ) : companies.filter(c => jobs.filter(j => j.company_id === c.id && !j.is_scheduled).length > 0).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 20px', background: 'var(--faculty-bg-alt)', borderRadius: '14px', border: '1px dashed var(--faculty-border)' }}>
                  <Building2 size={48} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: 'var(--faculty-text)' }}>No Active Openings</h4>
                  <p style={{ color: 'var(--faculty-muted)', margin: 0 }}>There are currently no active job openings from corporate partners.</p>
                </div>
              ) : (
                <div className="placement-company-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '20px' }}>
                  {companies.filter(c => jobs.filter(j => j.company_id === c.id && !j.is_scheduled).length > 0).map(comp => {
                    const compJobs = jobs.filter(j => j.company_id === comp.id && !j.is_scheduled);
                    const openingsCount = compJobs.length;
                    const compSelectedCount = compJobs.filter(j => bulkSelectedJobIds.includes(j.id)).length;
                    const isAllCompSelected = openingsCount > 0 && compSelectedCount === openingsCount;

                    return (
                      <div
                        key={comp.id}
                        onClick={() => setActiveCompanyCard(comp)}
                        style={{
                          background: compSelectedCount > 0 ? '#f0f9ff' : 'var(--card-bg)',
                          border: compSelectedCount > 0 ? '1.5px solid #05a8f3' : '1.5px solid var(--faculty-border)',
                          borderRadius: '16px',
                          padding: '22px',
                          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.03)',
                          cursor: 'pointer',
                          transition: 'all 0.25s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.borderColor = '#05a8f3';
                          e.currentTarget.style.boxShadow = '0 12px 28px rgba(5, 168, 243, 0.12)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = compSelectedCount > 0 ? '#05a8f3' : 'var(--faculty-border)';
                          e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.03)';
                        }}
                      >
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: compSelectedCount > 0 ? '#05a8f3' : openingsCount > 0 ? 'linear-gradient(90deg, #05a8f3, #3b82f6)' : '#cbd5e1' }} />

                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', marginTop: '6px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: openingsCount > 0 ? '#e0f2fe' : '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: openingsCount > 0 ? '#0284c7' : '#64748b', fontWeight: '800', fontSize: '1.2rem' }}>
                              <Building2 size={24} />
                            </div>
                            <span style={{
                              padding: '5px 12px',
                              borderRadius: '20px',
                              fontSize: '0.82rem',
                              fontWeight: '700',
                              background: openingsCount > 0 ? '#ecfdf5' : '#f8fafc',
                              color: openingsCount > 0 ? '#059669' : '#64748b',
                              border: openingsCount > 0 ? '1px solid #a7f3d0' : '1px solid #e2e8f0'
                            }}>
                              {openingsCount} {openingsCount === 1 ? 'Opening' : 'Openings'}
                            </span>
                          </div>

                          <h4 style={{ margin: '0 0 6px 0', fontSize: '1.2rem', fontWeight: '800', color: 'var(--faculty-text)' }}>
                            {comp.name}
                          </h4>
                          <span style={{ display: 'inline-block', fontSize: '0.84rem', color: '#05a8f3', fontWeight: '600', marginBottom: '12px' }}>
                            {comp.industry || 'Technology & AI'}
                          </span>
                          {comp.description && (
                            <p style={{ margin: '0 0 16px 0', fontSize: '0.85rem', color: 'var(--faculty-muted)', lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {comp.description}
                            </p>
                          )}
                        </div>

                        {openingsCount > 0 && (
                          <div
                            style={{
                              marginTop: '16px',
                              paddingTop: '14px',
                              borderTop: '1px solid var(--faculty-border)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '10px'
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            <span style={{ fontSize: '0.8rem', color: compSelectedCount > 0 ? '#05a8f3' : 'var(--faculty-muted)', fontWeight: '700' }}>
                              {compSelectedCount > 0 ? `${compSelectedCount} / ${openingsCount} Selected` : 'Select for Bulk Assign'}
                            </span>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  if (isAllCompSelected) {
                                    handleUnselectCompanyJobs(comp.id, e);
                                  } else {
                                    handleSelectCompanyJobs(comp.id, e);
                                  }
                                }}
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '8px',
                                  fontSize: '0.8rem',
                                  fontWeight: '700',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  background: isAllCompSelected ? '#05a8f3' : 'var(--faculty-bg-alt)',
                                  color: isAllCompSelected ? '#ffffff' : 'var(--faculty-text)',
                                  border: isAllCompSelected ? '1px solid #05a8f3' : '1px solid var(--faculty-border)',
                                  cursor: 'pointer',
                                  transition: 'all 0.15s'
                                }}
                              >
                                {isAllCompSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                                {isAllCompSelected ? 'Selected All' : `Bulk Assign (${openingsCount})`}
                              </button>
                              {compSelectedCount > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => handleUnselectCompanyJobs(comp.id, e)}
                                  style={{
                                    padding: '6px 10px',
                                    borderRadius: '8px',
                                    fontSize: '0.78rem',
                                    fontWeight: '700',
                                    background: 'transparent',
                                    color: '#ef4444',
                                    border: '1px solid #fca5a5',
                                    cursor: 'pointer'
                                  }}
                                >
                                  Unselect
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* LEVEL 2: Roles Posted for Selected Company (With Posted Dates & Detail View Action) */
            <div className="faculty-panel placement-roles-panel" style={{ padding: '24px', borderRadius: '16px', border: '1px solid var(--faculty-border)', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>

              {/* Breadcrumb Navigation Bar */}
              <div className="placement-drives-breadcrumb" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px', background: 'var(--faculty-bg-alt)', padding: '16px 20px', borderRadius: '12px', border: '1px solid var(--faculty-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    onClick={() => setActiveCompanyCard(null)}
                    style={{
                      background: '#ffffff',
                      border: '1.5px solid var(--faculty-border)',
                      padding: '8px 16px',
                      borderRadius: '10px',
                      color: 'var(--faculty-text)',
                      fontWeight: '700',
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                    }}
                  >
                    <ArrowLeft size={16} /> Back to Companies
                  </button>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '800', color: 'var(--faculty-text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Building2 size={22} style={{ color: '#05a8f3' }} /> {activeCompanyCard.name}
                    </h3>
                    <span style={{ fontSize: '0.85rem', color: 'var(--faculty-muted)' }}>
                      Showing posted roles and requirements ({jobs.filter(j => (activeCompanyCard.id === 'ALL' || j.company_id === activeCompanyCard.id) && !j.is_scheduled).length} active openings)
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {jobs.filter(j => (activeCompanyCard.id === 'ALL' || j.company_id === activeCompanyCard.id) && !j.is_scheduled).length > 0 && (
                    <>
                      <button
                        type="button"
                        className="faculty-btn-secondary"
                        style={{ padding: '8px 14px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        onClick={() => {
                          const levelJobIds = jobs.filter(j => (activeCompanyCard.id === 'ALL' || j.company_id === activeCompanyCard.id) && !j.is_scheduled).map(j => j.id);
                          const combined = Array.from(new Set([...bulkSelectedJobIds, ...levelJobIds]));
                          setBulkSelectedJobIds(combined);
                        }}
                      >
                        <CheckSquare size={15} /> Select All Roles Here
                      </button>

                      {bulkSelectedJobIds.length > 0 && (
                        <button
                          type="button"
                          className="faculty-btn-secondary"
                          style={{ padding: '8px 14px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#ef4444', borderColor: '#fca5a5' }}
                          onClick={() => {
                            const levelJobIds = jobs.filter(j => (activeCompanyCard.id === 'ALL' || j.company_id === activeCompanyCard.id)).map(j => j.id);
                            setBulkSelectedJobIds(bulkSelectedJobIds.filter(id => !levelJobIds.includes(id)));
                          }}
                        >
                          Unselect Here
                        </button>
                      )}
                    </>
                  )}
                  <UIElementGuard page="placement_drives" element="schedule_drive_btn">
                    <button
                      className="faculty-btn-primary"
                      style={{ padding: '8px 16px', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                      onClick={() => {
                        setSelectedCompanyForDrive(activeCompanyCard.id === 'ALL' ? (companies[0]?.id || null) : activeCompanyCard.id);
                        setSelectedJobForDrive(null);
                        setSelectedBatches(['All']);
                        setShowBatchModal(true);
                      }}
                    >
                      <Plus size={16} /> Approve Job
                    </button>
                  </UIElementGuard>
                </div>
              </div>

              {/* Roles List with Dates & Detail Action */}
              {loadingJobs ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--faculty-muted)' }}>Loading roles...</div>
              ) : jobs.filter(j => (activeCompanyCard.id === 'ALL' || j.company_id === activeCompanyCard.id) && !j.is_scheduled).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 20px', background: 'var(--faculty-bg-alt)', borderRadius: '14px', border: '1px dashed var(--faculty-border)' }}>
                  <Briefcase size={44} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: 'var(--faculty-text)' }}>No Active Roles Posted</h4>
                  <p style={{ color: 'var(--faculty-muted)', margin: '0 0 20px' }}>There are currently no unscheduled job openings listed under {activeCompanyCard.name}.</p>
                </div>
              ) : (
                <div className="placement-roles-table-wrap" style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden' }}>
                  <table className="placement-roles-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                        <th style={{ padding: '12px 16px', width: '40px' }}></th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}>ROLE</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}>LOCATION</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}>DATE POSTED</th>
                        <th style={{ padding: '12px 16px', paddingLeft: '40px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs
                        .filter(j => (activeCompanyCard.id === 'ALL' || j.company_id === activeCompanyCard.id) && !j.is_scheduled)
                        .map((job, idx, arr) => {
                          const postedDateStr = job.created_at
                            ? new Date(job.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'July 22, 2026';
                          const isBulkSelected = bulkSelectedJobIds.includes(job.id);
                          return (
                            <tr key={job.id}
                              style={{
                                background: isBulkSelected ? '#f0f9ff' : 'transparent',
                                borderBottom: idx < arr.length - 1 ? '1px solid var(--faculty-border)' : 'none',
                                transition: 'background 0.15s'
                              }}
                              onMouseEnter={e => { if (!isBulkSelected) e.currentTarget.style.background = 'var(--faculty-bg)'; }}
                              onMouseLeave={e => { if (!isBulkSelected) e.currentTarget.style.background = 'transparent'; }}
                            >
                              <td style={{ padding: '16px', verticalAlign: 'top', paddingTop: '20px' }}>
                                <div
                                  onClick={(e) => handleToggleBulkJob(job.id, e)}
                                  style={{
                                    width: '20px',
                                    height: '20px',
                                    borderRadius: '4px',
                                    border: isBulkSelected ? '2px solid #05a8f3' : '2px solid #cbd5e1',
                                    background: isBulkSelected ? '#05a8f3' : '#ffffff',
                                    color: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s'
                                  }}
                                  title={isBulkSelected ? "Unselect role" : "Select role for bulk schedule"}
                                >
                                  {isBulkSelected && <CheckSquare size={14} />}
                                </div>
                              </td>
                              <td style={{ padding: '16px', verticalAlign: 'top' }}>
                                <h4 style={{ margin: '0 0 4px 0', fontSize: '1.05rem', fontWeight: '800', color: 'var(--faculty-text)' }}>
                                  {job.job_name}
                                </h4>
                                {activeCompanyCard.id === 'ALL' && (
                                  <span style={{ fontSize: '0.88rem', display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#05a8f3', fontWeight: '700', marginTop: '4px' }}>
                                    <Building2 size={15} /> {job.company_name || 'Partner Company'}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '16px', verticalAlign: 'top' }}>
                                <span style={{ fontSize: '0.88rem', color: 'var(--faculty-text)', display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: '500', marginTop: '2px' }}>
                                  <MapPin size={15} style={{ color: '#64748b' }} /> {job.location || 'Remote/TBD'}
                                </span>
                              </td>
                              <td style={{ padding: '16px', verticalAlign: 'top' }}>
                                <span style={{ fontSize: '0.88rem', color: 'var(--faculty-text)', display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: '500', marginTop: '2px', whiteSpace: 'nowrap' }}>
                                  <Clock size={15} style={{ color: '#64748b' }} /> {postedDateStr}
                                </span>
                              </td>
                              <td style={{ padding: '16px', paddingLeft: '40px', verticalAlign: 'top', textAlign: 'left' }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-start', flexWrap: 'nowrap' }}>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedJobDetailModal(job)}
                                    style={{
                                      padding: '7px 12px',
                                      background: '#ffffff',
                                      border: '1.5px solid #05a8f3',
                                      color: '#05a8f3',
                                      borderRadius: '8px',
                                      fontWeight: '700',
                                      fontSize: '0.82rem',
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                      transition: 'all 0.15s',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.background = '#e8f7ff'; }}
                                    onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; }}
                                  >
                                    <Eye size={16} /> View Description & Details
                                  </button>

                                  <UIElementGuard page="placement_drives" element="schedule_drive_btn">
                                    <button
                                      type="button"
                                      className="faculty-btn-primary"
                                      onClick={() => {
                                        setSelectedJobForDrive(job.id);
                                        setSelectedCompanyForDrive(job.company_id || activeCompanyCard.id);
                                        setSelectedBatches(['All']);
                                        setShowBatchModal(true);
                                      }}
                                      style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                    >
                                      <CalendarCheck size={16} /> Approve
                                    </button>
                                  </UIElementGuard>

                                  <UIElementGuard page="placement_drives" element="reject_job_btn">
                                    <button
                                      type="button"
                                      onClick={() => handleRejectJob(job)}
                                      style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '0.82rem', display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'transparent', border: '1.5px solid #ef4444', color: '#ef4444', fontWeight: '700', cursor: 'pointer' }}
                                    >
                                      <XCircle size={16} /> Reject
                                    </button>
                                  </UIElementGuard>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {innerTab === 'scheduled' && (
        <div>
          {!activeScheduledCompanyCard ? (
            /* LEVEL 1: Scheduled Drives by Company */
            <div className="faculty-panel" style={{ padding: '24px', borderRadius: '16px', border: '1px solid var(--faculty-border)', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px', borderBottom: '1px solid var(--faculty-border)', paddingBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '800', color: 'var(--faculty-text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Building2 size={24} style={{ color: '#05a8f3' }} /> Approved Jobs Overview
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: 'var(--faculty-muted)' }}>
                    Select a company below to explore their approved job roles.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button
                    className="faculty-btn-secondary"
                    style={{ padding: '8px 14px', fontSize: '0.85rem' }}
                    onClick={() => setActiveScheduledCompanyCard('ALL')}
                  >
                    View All Approved Jobs
                  </button>
                  <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '8px', padding: '6px 12px' }}>
                    <Search size={16} color="var(--faculty-muted)" />
                    <input
                      type="text"
                      placeholder="Search company or role..."
                      value={scheduledSearchTerm}
                      onChange={e => setScheduledSearchTerm(e.target.value)}
                      style={{ border: 'none', outline: 'none', padding: '4px 8px', fontSize: '0.9rem', width: '200px' }}
                    />
                  </div>
                </div>
              </div>

              {loadingScheduled ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--faculty-muted)' }}>Loading...</div>
              ) : Object.keys(groupedScheduledDrives).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 20px', background: 'var(--faculty-bg-alt)', borderRadius: '14px', border: '1px dashed var(--faculty-border)' }}>
                  <CalendarCheck size={48} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: 'var(--faculty-text)' }}>No Drives Found</h4>
                  <p style={{ color: 'var(--faculty-muted)', margin: '0 0 20px' }}>No placement drives match your search or have been scheduled.</p>
                  {!scheduledSearchTerm && <button className="faculty-btn-primary" onClick={() => setInnerTab('drives')} style={{ padding: '10px 24px', fontSize: '0.95rem' }}>Go to Available Jobs</button>}
                </div>
              ) : scheduledSearchTerm ? (
                <div style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}>ROLE & DETAILS</th>
                        <th style={{ padding: '12px 16px', paddingLeft: '40px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredScheduledDrives.map((drive, idx, arr) => (
                        <tr key={drive.drive_id} style={{ borderBottom: idx < arr.length - 1 ? '1px solid var(--faculty-border)' : 'none', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--faculty-bg)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                          <td style={{ padding: '16px', verticalAlign: 'top' }}>
                            <h4 style={{ margin: '0 0 6px 0', color: 'var(--faculty-text)', fontSize: '1.1rem', fontWeight: '700' }}>{drive.job_name}</h4>
                            <div style={{ fontSize: '0.85rem', color: '#05a8f3', fontWeight: '600', marginBottom: '6px' }}>
                              <Building2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> {drive.company_name}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--faculty-muted)', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                              <span style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <strong>Target Batches:</strong>
                                {(drive.target_batch || 'All').split(',').map((b, i) => (
                                  <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>{b.trim()}</span>
                                ))}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '16px', paddingLeft: '40px', verticalAlign: 'top', textAlign: 'left' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', justifyContent: 'flex-start' }}>
                              <UIElementGuard page="placement_drives" element="view_candidates_btn">
                                <button
                                  type="button"
                                  style={{ padding: '7px 12px', fontSize: '0.82rem', background: '#f0f9ff', border: '1px solid #38bdf8', color: '#0284c7', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}
                                  onClick={() => {
                                    if (onViewCandidates) {
                                      onViewCandidates(drive.company_name, drive.job_name, drive);
                                    } else {
                                      console.warn("Candidates view function not provided.");
                                    }
                                  }}
                                >
                                  <Users size={14} /> View Applied Candidates ({drive.applied_count || 0})
                                </button>
                              </UIElementGuard>
                              <button
                                style={{ padding: '7px 12px', fontSize: '0.82rem', background: '#fff', border: '1px solid var(--faculty-accent)', color: 'var(--faculty-accent)', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}
                                onClick={() => {
                                  setSelectedDriveId(drive.drive_id);
                                  const batches = drive.target_batch ? drive.target_batch.split(',').map(s => s.trim()) : ['All'];
                                  setSelectedBatches(batches.length > 0 ? batches : ['All']);
                                  setShowUpdateBatchModal(true);
                                }}
                              >
                                <Award size={14} /> Edit Target Batch
                              </button>
                              <UIElementGuard page="placement_drives" element="unapprove_job_btn">
                                <button
                                  type="button"
                                  onClick={() => handleUnscheduleDrive(drive.drive_id)}
                                  style={{ padding: '7px 12px', fontSize: '0.82rem', background: '#fff', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}
                                  title="Unapprove Job"
                                >
                                  Unapprove
                                </button>
                              </UIElementGuard>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '20px' }}>
                  {Object.keys(groupedScheduledDrives).sort().map(companyName => {
                    const count = groupedScheduledDrives[companyName].length;

                    return (
                      <div
                        key={companyName}
                        onClick={() => setActiveScheduledCompanyCard(companyName)}
                        style={{
                          background: 'var(--card-bg)',
                          border: '1.5px solid var(--faculty-border)',
                          borderRadius: '16px',
                          padding: '22px',
                          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.03)',
                          cursor: 'pointer',
                          transition: 'all 0.25s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.borderColor = '#05a8f3';
                          e.currentTarget.style.boxShadow = '0 12px 28px rgba(5, 168, 243, 0.12)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = 'var(--faculty-border)';
                          e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.03)';
                        }}
                      >
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #05a8f3, #3b82f6)' }} />

                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', marginTop: '6px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#0284c7', fontWeight: '800', fontSize: '1.2rem' }}>
                              <Building2 size={24} />
                            </div>
                            <span style={{
                              padding: '5px 12px',
                              borderRadius: '20px',
                              fontSize: '0.82rem',
                              fontWeight: '700',
                              background: '#ecfdf5',
                              color: '#059669',
                              border: '1px solid #a7f3d0'
                            }}>
                              {count} {count === 1 ? 'Drive Scheduled' : 'Drives Scheduled'}
                            </span>
                          </div>

                          <h4 style={{ margin: '0 0 6px 0', fontSize: '1.2rem', fontWeight: '800', color: 'var(--faculty-text)' }}>
                            {companyName}
                          </h4>
                          <span style={{ display: 'inline-block', fontSize: '0.84rem', color: '#05a8f3', fontWeight: '600', marginBottom: '12px' }}>
                            View Details
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* LEVEL 2: Roles Scheduled for Selected Company */
            <div className="faculty-panel" style={{ padding: '24px', borderRadius: '16px', border: '1px solid var(--faculty-border)', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px', background: 'var(--faculty-bg-alt)', padding: '16px 20px', borderRadius: '12px', border: '1px solid var(--faculty-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    onClick={() => setActiveScheduledCompanyCard(null)}
                    style={{
                      background: '#ffffff',
                      border: '1.5px solid var(--faculty-border)',
                      padding: '8px 16px',
                      borderRadius: '10px',
                      color: 'var(--faculty-text)',
                      fontWeight: '700',
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                    }}
                  >
                    <ArrowLeft size={16} /> Back to Companies
                  </button>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '800', color: 'var(--faculty-text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Building2 size={22} style={{ color: '#05a8f3' }} /> {activeScheduledCompanyCard === 'ALL' ? 'All Approved Jobs' : activeScheduledCompanyCard}
                    </h3>
                    <span style={{ fontSize: '0.85rem', color: 'var(--faculty-muted)' }}>
                      {activeScheduledCompanyCard === 'ALL' ? 'Showing all approved jobs across all companies' : 'Showing approved jobs for this company'}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}>ROLE & DETAILS</th>
                      <th style={{ padding: '12px 16px', paddingLeft: '40px', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--faculty-muted)', letterSpacing: '0.6px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(activeScheduledCompanyCard === 'ALL' ? filteredScheduledDrives : (groupedScheduledDrives[activeScheduledCompanyCard] || [])).map((drive, idx, arr) => (
                      <tr key={drive.drive_id} style={{ borderBottom: idx < arr.length - 1 ? '1px solid var(--faculty-border)' : 'none', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--faculty-bg)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '16px', verticalAlign: 'top' }}>
                          <h4 style={{ margin: '0 0 6px 0', color: 'var(--faculty-text)', fontSize: '1.1rem', fontWeight: '700' }}>{drive.job_name}</h4>
                          {activeScheduledCompanyCard === 'ALL' && (
                            <div style={{ fontSize: '0.85rem', color: '#05a8f3', fontWeight: '600', marginBottom: '6px' }}>
                              <Building2 size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> {drive.company_name}
                            </div>
                          )}
                          <div style={{ fontSize: '0.85rem', color: 'var(--faculty-muted)', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                            <span style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                              <strong>Target Batches:</strong>
                              {(drive.target_batch || 'All').split(',').map((b, i) => (
                                <span key={i} style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>{b.trim()}</span>
                              ))}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '16px', paddingLeft: '40px', verticalAlign: 'top', textAlign: 'left' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', justifyContent: 'flex-start' }}>
                            <button
                              type="button"
                              style={{ padding: '7px 12px', fontSize: '0.82rem', background: '#f8fafc', border: '1px solid #cbd5e1', color: '#475569', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}
                              onClick={() => {
                                setSelectedJobDetailModal(drive);
                              }}
                            >
                              <FileText size={14} /> View Details
                            </button>
                            <UIElementGuard page="placement_drives" element="view_candidates_btn">
                              <button
                                type="button"
                                style={{ padding: '7px 12px', fontSize: '0.82rem', background: '#f0f9ff', border: '1px solid #38bdf8', color: '#0284c7', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}
                                onClick={() => {
                                  if (onViewCandidates) {
                                    onViewCandidates(activeScheduledCompanyCard === 'ALL' ? drive.company_name : activeScheduledCompanyCard, drive.job_name, drive);
                                  } else {
                                    console.warn("Candidates view function not provided.");
                                  }
                                }}
                              >
                                <Users size={14} /> View Applied Candidates ({drive.applied_count || 0})
                              </button>
                            </UIElementGuard>
                            <button
                              style={{ padding: '7px 12px', fontSize: '0.82rem', background: '#fff', border: '1px solid var(--faculty-accent)', color: 'var(--faculty-accent)', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', flexShrink: 0 }}
                              onClick={() => {
                                setSelectedDriveId(drive.drive_id);
                                const batches = drive.target_batch ? drive.target_batch.split(',').map(s => s.trim()) : ['All'];
                                setSelectedBatches(batches.length > 0 ? batches : ['All']);
                                setShowUpdateBatchModal(true);
                              }}
                            >
                              <Award size={14} /> Edit Target Batch
                            </button>
                            <UIElementGuard page="placement_drives" element="unapprove_job_btn">
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); handleUnscheduleDrive(drive.drive_id); }}
                                style={{ padding: '8px 16px', background: '#fff', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}
                                title="Unapprove Job"
                              >
                                Unapprove
                              </button>
                            </UIElementGuard>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {innerTab === 'all_scheduled' && (
        <div>
          {!activeScheduledCompanyCard ? (
            /* LEVEL 1: Scheduled Drives by Company */
            <div className="faculty-panel" style={{ padding: '24px', borderRadius: '16px', border: '1px solid var(--faculty-border)', boxShadow: '0 4px 16px rgba(0,0,0,0.03)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px', borderBottom: '1px solid var(--faculty-border)', paddingBottom: '16px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '800', color: 'var(--faculty-text)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Building2 size={24} style={{ color: '#10b981' }} /> Approved Jobs Overview
                  </h3>
                  <p style={{ margin: '4px 0 0 0', fontSize: '0.9rem', color: 'var(--faculty-muted)' }}>
                    Select a company below to explore their approved job roles.
                  </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '8px', padding: '6px 12px' }}>
                  <Search size={16} color="var(--faculty-muted)" />
                  <input
                    type="text"
                    placeholder="Search company or role..."
                    value={scheduledSearchTerm}
                    onChange={e => setScheduledSearchTerm(e.target.value)}
                    style={{ border: 'none', outline: 'none', padding: '4px 8px', fontSize: '0.9rem', width: '200px' }}
                  />
                </div>
              </div>

              {loadingScheduled ? (
                <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--faculty-muted)' }}>Loading...</div>
              ) : Object.keys(groupedScheduledDrives).length === 0 ? (
                <div style={{ textAlign: 'center', padding: '56px 20px', background: 'var(--faculty-bg-alt)', borderRadius: '14px', border: '1px dashed var(--faculty-border)' }}>
                  <CalendarCheck size={48} style={{ color: '#94a3b8', margin: '0 auto 12px' }} />
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '1.2rem', color: 'var(--faculty-text)' }}>No Drives Found</h4>
                  <p style={{ color: 'var(--faculty-muted)', margin: '0 0 20px' }}>No placement drives match your search or have been scheduled.</p>
                  {!scheduledSearchTerm && <button className="faculty-btn-primary" onClick={() => setInnerTab('drives')} style={{ padding: '10px 24px', fontSize: '0.95rem' }}>Approve First Job</button>}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: '20px' }}>
                  {Object.keys(groupedScheduledDrives).sort().map(companyName => {
                    const count = groupedScheduledDrives[companyName].length;

                    return (
                      <div
                        key={companyName}
                        onClick={() => setActiveScheduledCompanyCard(companyName)}
                        style={{
                          background: 'var(--card-bg)',
                          border: '1.5px solid var(--faculty-border)',
                          borderRadius: '16px',
                          padding: '22px',
                          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.03)',
                          cursor: 'pointer',
                          transition: 'all 0.25s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          position: 'relative',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.borderColor = '#10b981';
                          e.currentTarget.style.boxShadow = '0 12px 28px rgba(16, 185, 129, 0.12)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.borderColor = 'var(--faculty-border)';
                          e.currentTarget.style.boxShadow = '0 4px 14px rgba(0, 0, 0, 0.03)';
                        }}
                      >
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: 'linear-gradient(90deg, #10b981, #34d399)' }} />

                        <div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', marginTop: '6px' }}>
                            <div style={{ width: '48px', height: '48px', borderRadius: '12px', background: '#ecfdf5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', fontWeight: '800', fontSize: '1.2rem' }}>
                              <Building2 size={24} />
                            </div>
                            <span style={{
                              padding: '5px 12px',
                              borderRadius: '20px',
                              fontSize: '0.82rem',
                              fontWeight: '700',
                              background: '#ecfdf5',
                              color: '#059669',
                              border: '1px solid #a7f3d0'
                            }}>
                              {count} {count === 1 ? 'Drive Scheduled' : 'Drives Scheduled'}
                            </span>
                          </div>

                          <h4 style={{ margin: '0 0 6px 0', fontSize: '1.2rem', fontWeight: '800', color: 'var(--faculty-text)' }}>
                            {companyName}
                          </h4>
                          <span style={{ display: 'inline-block', fontSize: '0.84rem', color: '#10b981', fontWeight: '600', marginBottom: '12px' }}>
                            View Details
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            /* LEVEL 2: Roles Scheduled for Selected Company */
            <div className="faculty-panel" style={{ padding: '24px', borderRadius: '16px', border: '1px solid var(--faculty-border)', boxShadow: '0 4px 16px rgba(0,0,0,0.04)' }}>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px', background: 'var(--faculty-bg-alt)', padding: '16px 20px', borderRadius: '12px', border: '1px solid var(--faculty-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <button
                    onClick={() => setActiveScheduledCompanyCard(null)}
                    style={{
                      background: '#ffffff',
                      border: '1.5px solid var(--faculty-border)',
                      padding: '8px 16px',
                      borderRadius: '10px',
                      color: 'var(--faculty-text)',
                      fontWeight: '700',
                      fontSize: '0.88rem',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                    }}
                  >
                    <ArrowLeft size={16} /> Back to Companies
                  </button>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '800', color: 'var(--faculty-text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Building2 size={22} style={{ color: '#10b981' }} /> {activeScheduledCompanyCard}
                    </h3>
                    <span style={{ fontSize: '0.85rem', color: 'var(--faculty-muted)' }}>
                      Showing scheduled drives for this company
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {(groupedScheduledDrives[activeScheduledCompanyCard] || []).map(drive => (
                  <div key={drive.drive_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', border: '1px solid #d1fae5', borderRadius: '8px', background: '#f8fafc', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ flex: '1 1 300px' }}>
                      <h4 style={{ margin: '0 0 6px 0', color: 'var(--faculty-text)', fontSize: '1.1rem', fontWeight: '700' }}>{drive.job_name}</h4>
                      <div style={{ fontSize: '0.85rem', color: 'var(--faculty-muted)', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
                        <span>🏛 {drive.university_name}</span>
                        <span style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                          <strong>Target Batches:</strong>
                          {(drive.target_batch || 'All').split(',').map((b, i) => (
                            <span key={i} style={{ background: '#ecfdf5', color: '#059669', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600' }}>{b.trim()}</span>
                          ))}
                        </span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      {drive.created_at && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--faculty-faint)' }}>
                          {new Date(drive.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                      )}
                      <UIElementGuard page="placement_drives" element="unapprove_job_btn">
                        <button
                          type="button"
                          onClick={() => handleUnscheduleDrive(drive.drive_id)}
                          style={{ background: 'transparent', border: '1px solid #fca5a5', color: '#ef4444', padding: '4px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer' }}
                          title="Unapprove Job"
                        >
                          Unapprove
                        </button>
                      </UIElementGuard>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Schedule Placement Drive Modal */}
      {showBatchModal && (
        <div className="faculty-modal-overlay" onClick={() => setShowBatchModal(false)}>
          <div className="faculty-modal" onClick={e => e.stopPropagation()} style={{ width: '480px' }}>
            <div className="faculty-modal-header">
              <h2>Approve Job For Campus Drive</h2>
              <button className="faculty-close-btn" onClick={() => setShowBatchModal(false)}>✕</button>
            </div>
            <div className="faculty-modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>

              {/* Step 1: Select Company */}
              {selectedJobForDrive !== 'BULK' && (
                <>
                  <div className="faculty-form-group" style={{ margin: 0 }}>
                    <label style={{ fontWeight: '700', color: 'var(--faculty-text)', fontSize: '0.9rem', marginBottom: '6px', display: 'block' }}>
                      1. Select Company Partner <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <select
                      value={selectedCompanyForDrive || ''}
                      onChange={(e) => {
                        const compId = e.target.value ? Number(e.target.value) : null;
                        setSelectedCompanyForDrive(compId);
                        setSelectedJobForDrive(null);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--faculty-border)',
                        background: 'var(--faculty-panel)',
                        color: 'var(--faculty-text)',
                        fontSize: '0.92rem',
                        fontWeight: '600'
                      }}
                    >
                      <option value="">-- First Select a Company --</option>
                      {companies.map(comp => (
                        <option key={comp.id} value={comp.id}>
                          {comp.name} ({jobs.filter(j => j.company_id === comp.id && !j.is_scheduled).length} available roles)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Step 2: Select Assigned Job Role */}
                  <div className="faculty-form-group" style={{ margin: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label style={{ fontWeight: '700', color: 'var(--faculty-text)', fontSize: '0.9rem' }}>
                        2. Select Assigned Job Role <span style={{ color: '#ef4444' }}>*</span>
                      </label>
                    </div>
                    <select
                      value={selectedJobForDrive || ''}
                      onChange={(e) => setSelectedJobForDrive(e.target.value ? Number(e.target.value) : null)}
                      disabled={!selectedCompanyForDrive}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        border: '1px solid var(--faculty-border)',
                        background: !selectedCompanyForDrive ? 'var(--faculty-bg)' : 'var(--faculty-panel)',
                        color: !selectedCompanyForDrive ? 'var(--faculty-muted)' : 'var(--faculty-text)',
                        fontSize: '0.92rem',
                        fontWeight: '600',
                        cursor: !selectedCompanyForDrive ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {!selectedCompanyForDrive ? (
                        <option value="">-- Please select a company above first --</option>
                      ) : (
                        <>
                          <option value="">-- Select Job Role --</option>
                          {jobs
                            .filter(j => j.company_id === selectedCompanyForDrive && !j.is_scheduled)
                            .map(job => (
                              <option key={job.id} value={job.id}>
                                {job.job_name}
                              </option>
                            ))}
                        </>
                      )}
                    </select>
                    {selectedCompanyForDrive && jobs.filter(j => j.company_id === selectedCompanyForDrive && !j.is_scheduled).length === 0 && (
                      <div style={{ fontSize: '0.8rem', color: '#ef4444', marginTop: '6px' }}>
                        No unscheduled roles available for this company. Click "+ Add New Role" above to assign one.
                      </div>
                    )}
                  </div>
                </>
              )}

              {selectedJobForDrive === 'BULK' && (
                <div className="faculty-form-group" style={{ margin: 0 }}>
                  <label style={{ fontWeight: '700', color: 'var(--faculty-text)', fontSize: '0.9rem', marginBottom: '6px', display: 'block' }}>
                    1. Selected Roles for Bulk Drive
                  </label>
                  <div style={{ padding: '12px', background: 'var(--faculty-bg-alt)', borderRadius: '8px', border: '1px solid var(--faculty-border)', color: 'var(--faculty-text)', fontSize: '0.95rem' }}>
                    <strong>{bulkSelectedJobIds.length}</strong> job roles selected for bulk scheduling.
                  </div>
                </div>
              )}

              {/* Step 3: Select Target Batch */}
              <div className="faculty-form-group" style={{ margin: 0 }}>
                <label style={{ fontWeight: '700', color: 'var(--faculty-text)', fontSize: '0.9rem', marginBottom: '6px', display: 'block' }}>
                  {selectedJobForDrive === 'BULK' ? '2. Select Target Batches' : '3. Select Target Batches'}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--faculty-border)', padding: '12px', borderRadius: '8px', background: 'var(--faculty-bg-alt)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--faculty-text)', padding: '6px 0', borderBottom: '1px solid var(--faculty-border)' }}>
                    <input type="checkbox" checked={selectedBatches.includes('All')} onChange={() => handleBatchToggle('All')} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--faculty-accent)' }} />
                    <span style={{ fontWeight: selectedBatches.includes('All') ? '700' : '500' }}>All Batches</span>
                  </label>
                  {availableBatches.map(batch => (
                    <label key={batch.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--faculty-text)', padding: '4px 0' }}>
                      <input type="checkbox" checked={selectedBatches.includes('All') || selectedBatches.includes(batch.label)} onChange={() => handleBatchToggle(batch.label)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--faculty-accent)', flexShrink: 0 }} />
                      <span style={{ fontWeight: selectedBatches.includes(batch.label) ? '700' : '500', lineHeight: '1.3' }}>{batch.label}</span>
                    </label>
                  ))}
                </div>
              </div>

            </div>
            <div className="faculty-modal-footer">
              <button style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', color: '#475569' }} onClick={() => setShowBatchModal(false)}>Cancel</button>
              <button
                className="faculty-btn-primary"
                onClick={handleScheduleDrive}
                disabled={selectedJobForDrive === 'BULK' ? false : (!selectedJobForDrive || !selectedCompanyForDrive)}
                style={{
                  background: (selectedJobForDrive !== 'BULK' && (!selectedJobForDrive || !selectedCompanyForDrive)) ? '#94a3b8' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: '#fff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  cursor: (selectedJobForDrive !== 'BULK' && (!selectedJobForDrive || !selectedCompanyForDrive)) ? 'not-allowed' : 'pointer',
                  fontWeight: '600'
                }}
              >
                Approve Job
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Company Modal */}
      {showAddCompanyModal && (
        <div className="faculty-modal-overlay" onClick={() => setShowAddCompanyModal(false)}>
          <div className="faculty-modal" onClick={e => e.stopPropagation()} style={{ width: '440px' }}>
            <div className="faculty-modal-header">
              <h2>Add New Company Partner</h2>
              <button className="faculty-close-btn" onClick={() => setShowAddCompanyModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddCompany}>
              <div className="faculty-modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div className="faculty-form-group" style={{ margin: 0 }}>
                  <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>Company Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Google India Pvt Ltd"
                    value={newCompany.name}
                    onChange={e => setNewCompany({ ...newCompany, name: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--faculty-border)' }}
                  />
                </div>
                <div className="faculty-form-group" style={{ margin: 0 }}>
                  <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>Industry</label>
                  <input
                    type="text"
                    placeholder="e.g. Technology & AI"
                    value={newCompany.industry}
                    onChange={e => setNewCompany({ ...newCompany, industry: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--faculty-border)' }}
                  />
                </div>
                <div className="faculty-form-group" style={{ margin: 0 }}>
                  <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>Website</label>
                  <input
                    type="url"
                    placeholder="https://..."
                    value={newCompany.website}
                    onChange={e => setNewCompany({ ...newCompany, website: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--faculty-border)' }}
                  />
                </div>
                <div className="faculty-form-group" style={{ margin: 0 }}>
                  <label style={{ fontWeight: '600', marginBottom: '6px', display: 'block' }}>Description</label>
                  <textarea
                    rows={3}
                    placeholder="Brief description of the company..."
                    value={newCompany.description}
                    onChange={e => setNewCompany({ ...newCompany, description: e.target.value })}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid var(--faculty-border)', resize: 'vertical' }}
                  />
                </div>
              </div>
              <div className="faculty-modal-footer">
                <button type="button" style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', color: '#475569' }} onClick={() => setShowAddCompanyModal(false)}>Cancel</button>
                <button type="submit" className="faculty-btn-primary" style={{ padding: '8px 18px', borderRadius: '8px' }}>Create Company</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Job Role Modal (Removed) */}

      {/* Level 3: Job Role Detailed Description Modal */}
      {selectedJobDetailModal && (
        <div className="faculty-modal-overlay" onClick={() => setSelectedJobDetailModal(null)} style={{ zIndex: 1100 }}>
          <div className="faculty-modal" onClick={e => e.stopPropagation()} style={{ width: '850px', maxWidth: '94vw', borderRadius: '20px', overflow: 'hidden' }}>

            <div style={{ background: '#f8fafc', borderBottom: '1px solid var(--faculty-border)', padding: '24px 28px', color: 'var(--faculty-text)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <span style={{ display: 'inline-block', padding: '4px 12px', background: '#e0f2fe', color: '#0369a1', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '800', textTransform: 'uppercase', marginBottom: '10px' }}>
                  Job Specification
                </span>
                <h2 style={{ margin: '0 0 6px 0', fontSize: '1.5rem', fontWeight: '800', color: 'var(--faculty-text)' }}>
                  {selectedJobDetailModal.job_name}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#0369a1', fontSize: '0.95rem', fontWeight: '700' }}>
                  <Building2 size={16} /> {selectedJobDetailModal.company_name || activeCompanyCard?.name || 'Corporate Partner'}
                </div>
              </div>
              <button
                onClick={() => setSelectedJobDetailModal(null)}
                style={{ background: '#e2e8f0', border: 'none', color: '#475569', width: '36px', height: '36px', borderRadius: '50%', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: '28px', maxHeight: '70vh', overflowY: 'auto' }}>

              {/* Highlight Metrics Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '24px' }}>
                <div style={{ background: 'var(--faculty-bg-alt)', padding: '14px', borderRadius: '12px', border: '1px solid var(--faculty-border)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--faculty-muted)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <IndianRupee size={15} style={{ color: '#10b981' }} /> Salary Package / CTC
                  </span>
                  <strong style={{ fontSize: '1.05rem', color: 'var(--faculty-text)', fontWeight: '800' }}>
                    {selectedJobDetailModal.salary_package || 'As per industry standard'}
                  </strong>
                </div>

                <div style={{ background: 'var(--faculty-bg-alt)', padding: '14px', borderRadius: '12px', border: '1px solid var(--faculty-border)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--faculty-muted)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <MapPin size={15} style={{ color: '#ef4444' }} /> Job Location
                  </span>
                  <strong style={{ fontSize: '1.05rem', color: 'var(--faculty-text)', fontWeight: '800' }}>
                    {selectedJobDetailModal.location || 'Bengaluru / Hybrid'}
                  </strong>
                </div>


                <div style={{ background: 'var(--faculty-bg-alt)', padding: '14px', borderRadius: '12px', border: '1px solid var(--faculty-border)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--faculty-muted)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <Clock size={15} style={{ color: '#05a8f3' }} /> Requirement Posted On
                  </span>
                  <strong style={{ fontSize: '1.05rem', color: 'var(--faculty-text)', fontWeight: '800' }}>
                    {selectedJobDetailModal.created_at
                      ? new Date(selectedJobDetailModal.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'July 22, 2026'}
                  </strong>
                </div>
              </div>

              {/* Description Section */}
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '800', color: 'var(--faculty-text)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FileText size={18} style={{ color: '#05a8f3' }} /> Role Description & Requirements
                </h4>
                <div style={{ background: 'var(--card-bg)', padding: '18px', borderRadius: '12px', border: '1px solid var(--faculty-border)', fontSize: '0.94rem', lineHeight: '1.6', color: 'var(--faculty-text)' }}>
                  <div style={{ whiteSpace: 'pre-wrap', marginBottom: '16px' }}>
                    {selectedJobDetailModal.description || `We are looking for an exceptional candidate for the role of ${selectedJobDetailModal.job_name}. The selected candidate will collaborate with cross-functional technical teams and lead key engineering projects. Key qualifications include solid problem-solving abilities, domain expertise, and strong technical background.`}
                  </div>
                  {selectedJobDetailModal.responsibilities && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '15px', color: '#1e293b' }}>Key Responsibilities</h4>
                      <div style={{ whiteSpace: 'pre-wrap', color: '#475569' }}>
                        {selectedJobDetailModal.responsibilities}
                      </div>
                    </div>
                  )}

                  {selectedJobDetailModal.requirements && (
                    <div style={{ marginBottom: '20px' }}>
                      <h5 style={{ margin: '0 0 8px', fontSize: '0.9rem', color: '#475569', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Job Requirements</h5>
                      <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{selectedJobDetailModal.requirements}</p>
                    </div>
                  )}

                  {(selectedJobDetailModal.must_have_skills || selectedJobDetailModal.good_to_have_skills) && (
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                      {selectedJobDetailModal.must_have_skills && selectedJobDetailModal.must_have_skills.length > 0 && (
                        <div style={{ flex: '1', minWidth: '200px' }}>
                          <h5 style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#16a34a', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <CheckCircle size={15} /> Must Have Skills
                          </h5>
                          {(() => {
                            const skills = selectedJobDetailModal.must_have_skills;
                            if (typeof skills[0] === 'string') {
                              return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {skills.map((skillItem, idx) => (
                                    <span key={idx} style={{ background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', border: '1px solid #bbf7d0' }}>
                                      {skillItem}
                                    </span>
                                  ))}
                                </div>
                              );
                            }

                            const grouped = skills.reduce((acc, curr) => {
                              const cat = curr.categoryLabel || curr.category || 'Other Skills';
                              if (!acc[cat]) acc[cat] = [];
                              acc[cat].push(curr.skill);
                              return acc;
                            }, {});

                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                                {Object.entries(grouped).map(([cat, items]) => (
                                  <div key={cat}>
                                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>{cat}</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                      {items.map((skill, idx) => (
                                        <span key={`${cat}-${idx}`} style={{ background: '#dcfce7', color: '#166534', padding: '4px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', border: '1px solid #bbf7d0' }}>
                                          {skill}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {selectedJobDetailModal.good_to_have_skills && selectedJobDetailModal.good_to_have_skills.length > 0 && (
                        <div style={{ flex: '1', minWidth: '200px' }}>
                          <h5 style={{ margin: '0 0 10px', fontSize: '0.85rem', color: '#0ea5e9', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Lightbulb size={15} /> Good to Have Skills
                          </h5>
                          {(() => {
                            const skills = selectedJobDetailModal.good_to_have_skills;
                            if (typeof skills[0] === 'string') {
                              return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {skills.map((skillItem, idx) => (
                                    <span key={idx} style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', border: '1px solid #bae6fd' }}>
                                      {skillItem}
                                    </span>
                                  ))}
                                </div>
                              );
                            }

                            const grouped = skills.reduce((acc, curr) => {
                              const cat = curr.categoryLabel || curr.category || 'Other Skills';
                              if (!acc[cat]) acc[cat] = [];
                              acc[cat].push(curr.skill);
                              return acc;
                            }, {});

                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                                {Object.entries(grouped).map(([cat, items]) => (
                                  <div key={cat}>
                                    <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#64748b', marginBottom: '4px' }}>{cat}</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                      {items.map((skill, idx) => (
                                        <span key={`${cat}-${idx}`} style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 10px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: '600', border: '1px solid #bae6fd' }}>
                                          {skill}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Requirement scores: the college owns the numbers every student sees */}
              {(selectedJobDetailModal.must_have_skills?.length > 0 || selectedJobDetailModal.good_to_have_skills?.length > 0) && (
                <div style={{ marginTop: '18px', padding: '18px', borderRadius: '14px', border: '1px solid #e2e8f0', background: '#f8fafc' }}>
                  <SkillScoreEditor
                    mustNames={parseSkillNames(selectedJobDetailModal.must_have_skills)}
                    goodNames={parseSkillNames(selectedJobDetailModal.good_to_have_skills)}
                    scores={jobSkillDraft}
                    onChange={handleJobSkillChange}
                    onGenerate={generateJobSkillScores}
                    generating={jobSkillScoring}
                    readOnly={jobScoreReadOnly}
                    canGenerate={Boolean(String(selectedJobDetailModal.job_name || '').trim())}
                    helpText="Used as the “Job Requirement” levels in each student’s Skill Gap graph. Vendor numbers are kept unless you edit them; saving applies to future analyses only."
                    testId="job-skill-score-editor"
                  />
                  {selectedJobDetailModal.revision_pending && (
                    <p style={{ margin: '10px 0 0', fontSize: '12.5px', color: '#b45309', fontWeight: 600 }}>
                      Mantrika updated this job after it was approved — review the requirement scores.
                    </p>
                  )}
                  {!jobScoreReadOnly && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                      <button
                        type="button"
                        onClick={saveJobSkillScores}
                        disabled={jobSkillSaving}
                        className="faculty-btn-primary"
                        style={{ padding: '9px 18px', fontSize: '0.9rem', opacity: jobSkillSaving ? 0.7 : 1 }}
                      >
                        {jobSkillSaving ? 'Saving…' : 'Save requirement scores'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div style={{ background: 'var(--faculty-bg-alt)', padding: '18px 28px', borderTop: '1px solid var(--faculty-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => setSelectedJobDetailModal(null)}
                style={{ background: 'transparent', border: '1.5px solid var(--faculty-border)', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', color: 'var(--faculty-text)' }}
              >
                Close Specification
              </button>

              <button
                type="button"
                className="faculty-btn-primary"
                onClick={() => {
                  const targetJob = selectedJobDetailModal;
                  setSelectedJobDetailModal(null);
                  setSelectedJobForDrive(targetJob.id);
                  setSelectedCompanyForDrive(targetJob.company_id || activeCompanyCard?.id || null);
                  setSelectedBatches(['All']);
                  setShowBatchModal(true);
                }}
                style={{ padding: '11px 24px', fontSize: '0.96rem', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                <CalendarCheck size={18} /> Approve Job For Campus Drive
              </button>
            </div>

          </div>
        </div>
      )}

      {/* Update Target Batch Modal */}
      {showUpdateBatchModal && (
        <div className="faculty-modal-overlay" onClick={() => setShowUpdateBatchModal(false)}>
          <div className="faculty-modal" onClick={e => e.stopPropagation()} style={{ width: '400px' }}>
            <div className="faculty-modal-header">
              <h2>Update Target Batch</h2>
              <button className="faculty-close-btn" onClick={() => setShowUpdateBatchModal(false)}>✕</button>
            </div>
            <div className="faculty-modal-content">
              <div className="faculty-form-group">
                <label>Update Target Batches</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', maxHeight: '240px', overflowY: 'auto', border: '1px solid var(--faculty-border)', padding: '12px', borderRadius: '8px', background: 'var(--faculty-bg-alt)' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--faculty-text)', padding: '6px 0', borderBottom: '1px solid var(--faculty-border)' }}>
                    <input type="checkbox" checked={selectedBatches.includes('All')} onChange={() => handleBatchToggle('All')} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--faculty-accent)' }} />
                    <span style={{ fontWeight: selectedBatches.includes('All') ? '700' : '500' }}>All Batches</span>
                  </label>
                  {availableBatches.map(batch => (
                    <label key={batch.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--faculty-text)', padding: '4px 0' }}>
                      <input type="checkbox" checked={selectedBatches.includes('All') || selectedBatches.includes(batch.label)} onChange={() => handleBatchToggle(batch.label)} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--faculty-accent)', flexShrink: 0 }} />
                      <span style={{ fontWeight: selectedBatches.includes(batch.label) ? '700' : '500', lineHeight: '1.3' }}>{batch.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="faculty-modal-footer">
              <button style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', color: '#475569' }} onClick={() => setShowUpdateBatchModal(false)}>Cancel</button>
              <button className="faculty-btn-primary" onClick={handleUpdateBatch} style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Unschedule Modal */}
      {showUnscheduleModal && (
        <div className="faculty-modal-overlay" onClick={() => setShowUnscheduleModal(false)}>
          <div className="faculty-modal" onClick={e => e.stopPropagation()} style={{ width: '400px' }}>
            <div className="faculty-modal-header">
              <h2>Confirm Unapprove</h2>
              <button className="faculty-close-btn" onClick={() => setShowUnscheduleModal(false)}>✕</button>
            </div>
            <div className="faculty-modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <p style={{ margin: 0, color: 'var(--faculty-text)', fontSize: '0.95rem' }}>
                Are you sure you want to unschedule this placement drive? It will be removed from the calendar and the roles will be returned to the available jobs pool.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button type="button" style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', color: '#475569' }} onClick={() => setShowUnscheduleModal(false)}>Cancel</button>
                <button type="button" style={{ background: '#ef4444', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }} onClick={confirmUnscheduleDrive}>Yes, Unapprove</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Reject Modal */}
      {jobToReject && (
        <div className="faculty-modal-overlay" onClick={() => setJobToReject(null)}>
          <div className="faculty-modal" onClick={e => e.stopPropagation()} style={{ width: '400px' }}>
            <div className="faculty-modal-header">
              <h2>Confirm Reject Job</h2>
              <button className="faculty-close-btn" onClick={() => setJobToReject(null)}>✕</button>
            </div>
            <div className="faculty-modal-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <p style={{ margin: 0, color: 'var(--faculty-text)', fontSize: '0.95rem' }}>
                Are you sure you want to reject <strong>{jobToReject.job_name}</strong>? It will be permanently hidden from the available jobs list.
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                <button type="button" style={{ background: 'transparent', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', color: '#475569' }} onClick={() => setJobToReject(null)}>Cancel</button>
                <button type="button" style={{ background: '#ef4444', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '700' }} onClick={confirmRejectJob}>Yes, Reject</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlacementDrivesModule;
